import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationPage from '../components/NotificationPage';
import { Button } from '@/components/ui/button';
import { getUsername } from '../utils/userUtils';
import { useAuth } from '../contexts/AuthContext';
import { consumeUnauthorizeReturnPath } from '@/utils/authRedirect';

const TOKEN_CHECK_INTERVAL_MS = 5000;

const getNameFromUser = (user: unknown): string | undefined => {
  if (!user || typeof user !== 'object') return undefined;
  const record = user as Record<string, unknown>;

  const username = record.username;
  if (typeof username === 'string' && username.trim()) return username;

  const name = record.name;
  if (typeof name === 'string' && name.trim()) return name;

  return undefined;
};

const Unauthorized = () => {
  const { user, microsoftToken, isAuthenticated, login, getGromToken, teamsSignInRequired, signInWithTeamsPopup } = useAuth();
  const navigate = useNavigate();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);

  // Prefer MSAL account (accounts[0]) when available; fall back to stored account info (Teams flow)
  const userName =
    getNameFromUser(user) ||
    getUsername() ||
    'Unknown User';

  // This page can be hit directly (outside ProtectedRoute), so microsoftToken may not
  // have been acquired yet. Trigger the same silent/interactive login ProtectedRoute uses.
  useEffect(() => {
    if (!isAuthenticated) {
      login().catch((error) => console.error('Login failed on unauthorize page:', error));
    }
  }, [isAuthenticated, login]);

  // Poll /token every 5s; once it returns 200, the session is healthy again → go back.
  // Goes through AuthContext.getGromToken so gromToken state/localStorage is set too,
  // otherwise ProtectedRoute would keep showing its own loading screen after navigate.
  useEffect(() => {
    if (!microsoftToken) return;

    let cancelled = false;

    const checkToken = async () => {
      try {
        await getGromToken(microsoftToken);
        if (cancelled) return;
        navigate(consumeUnauthorizeReturnPath(), { replace: true });
      } catch {
        // still unauthorized, keep polling
      }
    };

    const intervalId = window.setInterval(checkToken, TOKEN_CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [microsoftToken, getGromToken, navigate]);

  // Silent Teams sign-in needed the user: sign in through the Teams popup, then go back where they were.
  // Started from a click so the popup is not blocked.
  const handleTeamsSignIn = async () => {
    setIsSigningIn(true);
    setSignInError(null);
    try {
      await signInWithTeamsPopup();
      navigate(consumeUnauthorizeReturnPath(), { replace: true });
    } catch (error) {
      setSignInError(error instanceof Error ? error.message : 'Microsoft sign-in failed');
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <NotificationPage
      content={
        <>
          <p>User {userName} lacks of privilege to access reports.</p>
          <p>Please contact your administrator for further information.</p>
          {teamsSignInRequired && (
            <>
              <p>Or sign in with your Microsoft account.</p>
              <Button type="button" onClick={handleTeamsSignIn} disabled={isSigningIn}>
                {isSigningIn ? 'Signing in…' : 'Sign in with Microsoft'}
              </Button>
              {signInError && <p role="alert">{signInError}</p>}
            </>
          )}
        </>
      }
      enableRotation={false}
    />
  );
};

export default Unauthorized;
